// src/App.js — Route definitions + AppShell wrapper
import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { getUser, getToken, setToken, removeToken, logout } from './utils/auth';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import TicketsPage from './pages/TicketsPage';
import NewTicketPage from './pages/NewTicketPage';
import TicketDetailPage from './pages/TicketDetailPage';
import TicketTrackPage from './pages/TicketTrackPage';
import AdminPage from './pages/AdminPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import AiResolvedPage from './pages/AiResolvedPage';
import LdapSettingsPage from './pages/LdapSettingsPage';
import UserManagementPage from './pages/UserManagementPage';
import MyQueuePage from './pages/MyQueuePage';
import AiResultPage from './pages/AiResultPage';
import TeamPerformancePage from './pages/TeamPerformancePage';

// Shared layout
import AppShell from './components/AppShell';
import Toast from './components/Toast';

function PrivateRoute({ element, adminOnly = false }) {
  const user = getUser();
  const location = useLocation();
  if (!getToken()) {
    const redirectPath = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirectPath}`} replace />;
  }
  if (adminOnly && !['admin', 'it'].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  return element;
}

export default function App() {
  const [user, setUser] = useState(getUser);
  const [toast, setToast] = useState({ msg: null, type: 'default' });
  const navigate = useNavigate();
  const location = useLocation();

  const toastTimer = React.useRef(null);
  const showToast = useCallback((msg, type = 'default') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast({ msg: null, type: 'default' }), 4500);
  }, []);

  const handleLogin = useCallback((token, shouldNavigate = true) => {
    setToken(token);
    setUser(getUser());

    if (!shouldNavigate) return;

    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    navigate(redirect || '/dashboard');
  }, [navigate]);

  const handleLogout = useCallback(async () => {
    await logout();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async (manualToken) => {
    if (manualToken) {
      handleLogin(manualToken, false);
      return;
    }
    try {
      const { apiFetch } = await import('./utils/api');
      const res = await apiFetch('/api/refresh', {
        method: 'POST',
        credentials: 'include'
      });
      if (res && res.token) {
        handleLogin(res.token, false);
      }
    } catch (err) {
      console.error('Refresh user error:', err);
    }
  }, [handleLogin]);

  // Auth pages share no shell
  const isAuthPage = ['/login', '/register'].includes(location.pathname);

  return (
    <>
      <Toast message={toast.msg} type={toast.type} />
      {isAuthPage ? (
        <Routes>
          <Route path="/login" element={<LoginPage onLogin={handleLogin} showToast={showToast} />} />
          <Route path="/register" element={<RegisterPage showToast={showToast} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      ) : (
        <AppShell user={user} onLogout={handleLogout} currentPath={location.pathname} onRefreshUser={refreshUser}>
          <Routes>
            <Route path="/dashboard" element={<PrivateRoute element={<DashboardPage showToast={showToast} />} />} />
            <Route path="/tickets" element={<PrivateRoute element={<TicketsPage showToast={showToast} />} />} />
            <Route path="/admin/tickets" element={<PrivateRoute adminOnly element={<TicketsPage showToast={showToast} />} />} />
            <Route path="/tickets/new" element={<PrivateRoute element={<NewTicketPage showToast={showToast} />} />} />
            <Route path="/tickets/:id/track" element={<PrivateRoute element={<TicketTrackPage showToast={showToast} />} />} />
            <Route path="/tickets/:id" element={<PrivateRoute element={<TicketDetailPage showToast={showToast} />} />} />
            <Route path="/admin" element={<PrivateRoute adminOnly element={<AdminPage showToast={showToast} />} />} />
            <Route path="/admin/performance" element={<PrivateRoute adminOnly element={<TeamPerformancePage showToast={showToast} />} />} />
            <Route path="/admin/ai-resolved" element={<PrivateRoute adminOnly element={<AiResolvedPage showToast={showToast} />} />} />
            <Route path="/admin/ldap" element={<PrivateRoute adminOnly element={<LdapSettingsPage showToast={showToast} />} />} />
            <Route path="/admin/users" element={<PrivateRoute adminOnly element={<UserManagementPage showToast={showToast} />} />} />
            <Route path="/knowledge-base" element={<PrivateRoute element={<KnowledgeBasePage showToast={showToast} />} />} />
            <Route path="/my-queue" element={<PrivateRoute adminOnly element={<MyQueuePage showToast={showToast} />} />} />
            <Route path="/ai-result" element={<PrivateRoute element={<AiResultPage showToast={showToast} />} />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AppShell>
      )}
    </>
  );
}
